import { Button, Checkbox, Table } from 'antd'
import client from 'apollo'
import { AppContext } from 'AppContext'
import { CREATE_TICKET } from 'modules/Tickets/graphql/Mutations'
import { UPDATE_USER } from 'modules/Users/graphql/Mutations'
import React, { useContext, useEffect, useState } from 'react'
import {
  GET_RISK_PROFILE_SUMMARY,
  GET_SP_AGREEMENT_DATA,
  GET_USER_ANSWERS,
} from '../graphql/Queries'
import { convertNumberToCommaSeparate } from 'utils/utilities'
import {
  DELETE_LAST_RISK_PROFILE_SUMMARY,
  RISK_ASSESSMENT_MAIL,
  SENDING_ASSESSMENT_MAILS,
} from '../graphql/Mutations'
import {
  GET_PLANS_BASED_POINTS,
  GET_QUESTIONS,
} from 'modules/Questionnaires/graphql/Query'
import { useQuery } from '@apollo/react-hooks'
import moment from 'moment'

const NewSummaryUIModal = ({ setCurrent, showAgreement }) => {
  const {
    dispatch,
    state: { currentUser },
  } = useContext(AppContext)
  const { id } = currentUser
  const [agree, setAgree] = useState(false)
  const [agreeLoading, setAgreeLoading] = useState(false)
  const [disagreeLoading, setDisagreeLoading] = useState(false)
  const [summaryData, setSummaryData] = useState()
  const [filteredAnswers, setFilteredAnswers] = useState([])
  const [effectiveAnswers, setEffectiveAnswers] = useState([])

  const { data: suggestPlans } = useQuery(GET_PLANS_BASED_POINTS, {
    variables: { points: currentUser?.clientScore },
    fetchPolicy: 'network-only',
  })
  let plans = JSON.stringify(suggestPlans?.fetchPlansBasedOnPoints)

  const { data: userAnswers } = useQuery(GET_USER_ANSWERS, {
    variables: { where: { planName: 'Power Booster Plan' } },
    fetchPolicy: 'network-only',
  })
  const { data: questions } = useQuery(GET_QUESTIONS, {
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    const pbAnswers = userAnswers?.getUserAnswers || []
    if (pbAnswers.length) {
      setEffectiveAnswers(pbAnswers)
      return
    }

    // Keep Power Booster as primary source; fallback only to avoid empty modal.
    client
      .query({
        query: GET_USER_ANSWERS,
        variables: { where: {} },
        fetchPolicy: 'network-only',
      })
      .then((res) => {
        setEffectiveAnswers(res?.data?.getUserAnswers || [])
      })
      .catch(() => {
        setEffectiveAnswers([])
      })
  }, [userAnswers])

  useEffect(() => {
    const filteredData = effectiveAnswers
      ?.filter((userAnswer) =>
        questions?.getQuestions?.some(
          (question) => question.id === userAnswer.question.id
        )
      )
      ?.map((userAnswer) => {
        const matchingQuestion = questions?.getQuestions?.find(
          (question) => question.id === userAnswer.question.id
        )
        return {
          ...userAnswer,
          questionText: matchingQuestion?.question,
        }
      })
    setFilteredAnswers(filteredData)
  }, [questions, effectiveAnswers])

  let maxAUALimit = 0

  filteredAnswers &&
    filteredAnswers.forEach((item) => {
      const selectedOptions = JSON.parse(item.selectedOptions)
      selectedOptions.forEach((option) => {
        const calculatedValue = option.value * option.multiplication
        if (calculatedValue > maxAUALimit) {
          maxAUALimit = calculatedValue
        }
      })
    })

  useEffect(() => {
    client
      .query({
        query: GET_RISK_PROFILE_SUMMARY,
        fetchPolicy: 'network-only',
      })
      .then((res) => {
        setSummaryData(res?.data)
      })
  }, [currentUser])

  const columns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 230,
    },
    {
      title: 'Old Data',
      dataIndex: 'oldData',
      key: 'oldData',
      width: 300,
    },
    {
      title: 'New Data',
      dataIndex: 'newData',
      key: 'newData',
      width: 300,
    },
    {
      title: 'Action Remark',
      dataIndex: 'actionRemark',
      key: 'actionRemark',
      width: 230,
    },
  ]
  const data = [
    {
      key: '1',
      type: 'Risk Profile Score',
      oldData:
        summaryData?.getRiskProfileSummaryUi?.oldSummary?.RiskProfileScore ||
        currentUser?.clientScore,
      newData:
        summaryData?.getRiskProfileSummaryUi?.latestSummary?.RiskProfileScore,
      actionRemark:
        (summaryData?.getRiskProfileSummaryUi?.oldSummary?.RiskProfileScore ||
          currentUser?.clientScore) ===
          summaryData?.getRiskProfileSummaryUi?.latestSummary?.RiskProfileScore
          ? 'No Change'
          : (summaryData?.getRiskProfileSummaryUi?.oldSummary
            ?.RiskProfileScore || currentUser?.clientScore) >
            summaryData?.getRiskProfileSummaryUi?.latestSummary
              ?.RiskProfileScore
            ? 'Reduced Risk'
            : 'Increased Risk',
    },

    {
      key: '2',
      type: 'Allowed Investment Plan',
      oldData: (() => {
        try {
          const oldPlans = JSON.parse(
            summaryData?.getRiskProfileSummaryUi?.oldSummary?.suggestedPlans ||
            plans
          )
          return Array.isArray(oldPlans)
            ? oldPlans.map((plan) => plan.name).join(', ')
            : 'N/A'
        } catch (error) {
          return 'N/A'
        }
      })(),

      newData: (() => {
        try {
          const newPlans = JSON.parse(
            summaryData?.getRiskProfileSummaryUi?.latestSummary?.suggestedPlans
          )
          return Array.isArray(newPlans)
            ? newPlans.map((plan) => plan.name).join(', ')
            : 'N/A'
        } catch (error) {
          return 'N/A'
        }
      })(),

      actionRemark: (() => {
        try {
          const oldPlans = JSON.parse(
            summaryData?.getRiskProfileSummaryUi?.oldSummary?.suggestedPlans ||
            plans
          )
          const newPlans = JSON.parse(
            summaryData?.getRiskProfileSummaryUi?.latestSummary?.suggestedPlans
          )
          return oldPlans.length > newPlans.length
            ? 'Limited due to lower risk score'
            : oldPlans.length < newPlans.length
              ? 'Expanded Options'
              : 'No Change'
        } catch (error) {
          return 'Expanded Options'
        }
      })(),
    },
    {
      key: '3',
      type: 'Maximum Exposure Limit',
      oldData:
        convertNumberToCommaSeparate(
          summaryData?.getRiskProfileSummaryUi?.oldSummary?.maxAUALimit
        ) || convertNumberToCommaSeparate(maxAUALimit),
      newData: convertNumberToCommaSeparate(
        summaryData?.getRiskProfileSummaryUi?.latestSummary?.maxAUALimit
      ),
      actionRemark: (() => {
        const oldLimit =
          summaryData?.getRiskProfileSummaryUi?.oldSummary?.maxAUALimit ||
          maxAUALimit
        const newLimit =
          summaryData?.getRiskProfileSummaryUi?.latestSummary?.maxAUALimit

        if (newLimit < oldLimit) return 'Reduced Risk'
        if (newLimit > oldLimit) return 'Increased Risk'
        return 'No Change'
      })(),
    },
    {
      key: '5',
      type: 'Exposure Adjustment Required?',
      oldData:
        (summaryData?.getRiskProfileSummaryUi?.oldSummary?.currentAUA ||
          currentUser?.exposure) >
          (summaryData?.getRiskProfileSummaryUi?.oldSummary?.maxAUALimit ||
            maxAUALimit)
          ? 'Yes'
          : 'No',
      newData: (() => {
        const auaDifference =
          summaryData?.getRiskProfileSummaryUi?.latestSummary?.auaDifference

        return auaDifference > 0 ? 'Yes' : 'No'
      })(),

      actionRemark: (() => {
        const auaDifference =
          summaryData?.getRiskProfileSummaryUi?.latestSummary?.auaDifference

        if (auaDifference > 0) {
          return `${convertNumberToCommaSeparate(
            auaDifference
          )} Advised to reduce Exposure`
        } else {
          return 'No Action needed'
        }
      })(),
    },
  ]
  const handleAgree = (e) => {
    setAgree(e.target.checked)
  }
  const onAgree = () => {
    setAgreeLoading(true)
    if (
      summaryData?.getRiskProfileSummaryUi?.latestSummary?.auaDifference > 0
    ) {
      if (!showAgreement) {
        client
          .query({
            query: GET_SP_AGREEMENT_DATA,
            variables: {
              where: {
                dontBuyEquity: 'YES',
              },
            },
          })
          .then((res) => {
            client
              .mutate({
                mutation: UPDATE_USER,
                variables: {
                  data: {
                    revisedRiskProfileSteps: 'DONE',
                    ...(!showAgreement && {
                      revisedRiskButtonInfo: JSON.stringify({
                        isFromRevisedRiskProfile: false,
                        lastUpdatedDate: moment().format(),
                      }),
                    }),
                  },
                  id: currentUser?.id,
                },
              })
              .then((res) => {
                client.mutate({ mutation: RISK_ASSESSMENT_MAIL }).then(() => {
                  client.mutate({
                    mutation: SENDING_ASSESSMENT_MAILS,
                  })
                })
              })
              // })
              .then((res) => {
                client.mutate({
                  mutation: CREATE_TICKET,
                  variables: {
                    data: {
                      categoryId: 4, //Update Advisory/trade Data
                      subCategoryId: 45, // Other
                      generatedFor: +id,
                      isForClient: true,
                      description: `${convertNumberToCommaSeparate(
                        summaryData?.getRiskProfileSummaryUi?.latestSummary
                          ?.auaDifference
                      )} Advised to reduce Exposure`,
                    },
                  },
                })
              })
              .then((res) => {
                client.query({
                  query: GET_SP_AGREEMENT_DATA,
                  variables: {
                    where: {
                      dontBuyEquity: 'YES',
                    },
                  },
                })
                client
                  .mutate({
                    mutation: UPDATE_USER,
                    variables: {
                      data: {
                        revisedRiskProfileSteps: 'DONE',
                        ...(!showAgreement && {
                          revisedRiskButtonInfo: JSON.stringify({
                            isFromRevisedRiskProfile: false,
                            lastUpdatedDate: moment().format(),
                          }),
                        }),
                      },
                      id: currentUser?.id,
                    },
                  })

                  .then((res) => {
                    dispatch({
                      type: 'CURRENT_USER',
                      data: res.data.updateUser,
                    })

                    setCurrent(2)
                  })
              })
          })
      } else {
        client
          .mutate({
            mutation: UPDATE_USER,
            variables: {
              data: {
                revisedRiskProfileSteps: 'AGREEMENT_SIGN',
                ...(!showAgreement && {
                  revisedRiskButtonInfo: JSON.stringify({
                    isFromRevisedRiskProfile: false,
                    lastUpdatedDate: moment().format(),
                  }),
                }),
              },
              id: currentUser?.id,
            },
          })
          .then((res) => {
            client.mutate({ mutation: RISK_ASSESSMENT_MAIL }).then(() => {
              client.mutate({
                mutation: SENDING_ASSESSMENT_MAILS,
              })
            })
          })
          // })
          .then((res) => {
            client.mutate({
              mutation: CREATE_TICKET,
              variables: {
                data: {
                  categoryId: 4, //Update Advisory/trade Data
                  subCategoryId: 45, // Other
                  generatedFor: +id,
                  isForClient: true,
                  description: `${convertNumberToCommaSeparate(
                    summaryData?.getRiskProfileSummaryUi?.latestSummary
                      ?.auaDifference
                  )} Advised to reduce Exposure`,
                },
              },
            })
          })
          .then((res) => {
            client
              .mutate({
                mutation: UPDATE_USER,
                variables: {
                  data: {
                    revisedRiskProfileSteps: 'DONE',
                    ...(!showAgreement && {
                      revisedRiskButtonInfo: JSON.stringify({
                        isFromRevisedRiskProfile: false,
                        lastUpdatedDate: moment().format(),
                      }),
                    }),
                  },
                  id: currentUser?.id,
                },
              })

              .then((res) => {
                dispatch({
                  type: 'CURRENT_USER',
                  data: res.data.updateUser,
                })

                setCurrent(2)
              })
          })
      }
    } else {
      client
        .query({
          query: GET_SP_AGREEMENT_DATA,
          variables: {
            where: {
              dontBuyEquity: 'YES',
            },
          },
        })
        .then((res) => {
          client
            .mutate({
              mutation: UPDATE_USER,
              variables: {
                data: {
                  revisedRiskProfileSteps: showAgreement
                    ? 'AGREEMENT_SIGN'
                    : 'DONE',
                  ...(!showAgreement && {
                    revisedRiskButtonInfo: JSON.stringify({
                      isFromRevisedRiskProfile: false,
                      lastUpdatedDate: moment().format(),
                    }),
                  }),
                },
                id: currentUser?.id,
              },
            })
            .then((res) => {
              dispatch({
                type: 'CURRENT_USER',
                data: res.data.updateUser,
              })
              setCurrent(2)
              client.mutate({ mutation: RISK_ASSESSMENT_MAIL }).then(() => {
                client.mutate({
                  mutation: SENDING_ASSESSMENT_MAILS,
                })
              })
            })
        })
      //client
      //.mutate({
      //  mutation: UPDATE_USER,
      //  variables: {
      //    data: {
      //      revisedRiskProfileSteps: showAgreement
      //        ? 'AGREEMENT_SIGN'
      //        : 'DONE',
      //      ...(!showAgreement && {
      //        revisedRiskButtonInfo: JSON.stringify({
      //          isFromRevisedRiskProfile: false,
      //          lastUpdatedDate: moment().format(),
      //        }),
      //      }),
      //    },
      //    id: currentUser?.id,
      //  },
      //})
      //.then((res) => {
      //  dispatch({
      //    type: 'CURRENT_USER',
      //    data: res.data.updateUser,
      //  })
      //  setCurrent(2)
      //  client.mutate({ mutation: RISK_ASSESSMENT_MAIL }).then(() => {
      //    client.mutate({
      //      mutation: SENDING_ASSESSMENT_MAILS,
      //    })
      //  })
      //})
    }
  }
  const onDisagree = () => {
    setDisagreeLoading(true)
    client
      .mutate({
        mutation: DELETE_LAST_RISK_PROFILE_SUMMARY,
      })
      .then((res) => {
        client
          .mutate({
            mutation: UPDATE_USER,
            variables: {
              id,
              data: {
                revisedRiskProfileSteps: 'RISK_PROFILE',
              },
            },
          })
          .then((res) => {
            dispatch({
              type: 'CURRENT_USER',
              data: res.data.updateUser,
            })
          })
      })
  }
  return (
    <div>
      {summaryData && (
        <div
          style={{
            width: '100% !important',
          }}
        >
          <Table
            columns={columns}
            dataSource={data}
            bordered
            pagination={{ pageSize: 1000, hideOnSinglePage: true }}
            className='advisory_update_table'
            style={{ overflow: 'auto' }}
          />
          <br />
          <div className='agree-wrapper'>
            <Checkbox onChange={handleAgree} />
            <p className='agree-content'>
              {summaryData?.getRiskProfileSummaryUi?.oldSummary
                ?.RiskProfileScore >
                summaryData?.getRiskProfileSummaryUi?.latestSummary
                  ?.RiskProfileScore
                ? `Please confirm your consent to proceed with these changes. You are initiating investment exposure in access of your net worth as per the risk profile with Finideas. You are requested to either reduce the investment exposure or can review the risk profile with us.`
                : `Please confirm your consent to proceed with these changes. You are initiating investment exposure in access of your net worth as per the risk profile with Finideas. You are requested to either reduce the investment exposure or can review the risk profile with us.`}
            </p>
          </div>
          <div className='footer-btn-div'>
            <Button
              className='footer-cancel-btn'
              onClick={() => onAgree()}
              disabled={!agree}
              style={{
                backgroundColor: '#34a853',
                color: 'white',
              }}
              loading={agreeLoading}
            >
              Agree
            </Button>
            <Button
              className='footer-submit-btn'
              onClick={() => onDisagree()}
              type='primary'
              disabled={!agree}
              loading={disagreeLoading}
            >
              Disagree
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
export default NewSummaryUIModal
